import React, {useEffect, useRef, useState} from "react";
import {engineUrl} from "../../common/engineLink";

// Zed's introductions on the big screen: Zed (camera) in the middle, the whole cast as a gallery on
// both sides, the player he's talking about pulled forward as a card beside him, subtitles above.
// Everyone is on screen even though he only gets to some of them; whoever he's done with is marked filed.
const W = 1920, H = 1080, CAM_W = 540, CAM_H = 720, CAM_X = (W - CAM_W) / 2, CAM_Y = 290;
const ZED_RED = '255,42,16';

// Where the Zed cam comes from: 'sim' (his face, mirrored live; the preview) or 'camera' (a real camera
// on his physical face). ?zedcam= or localStorage zedcam overrides; the preview defaults to the sim.
const camSource = () => {
    const q = new URLSearchParams(window.location.search).get('zedcam');
    let saved = null;
    try { saved = localStorage.getItem('zedcam'); } catch (e) {}
    return q || saved || (window.location.port === '4568' ? 'sim' : 'camera');
};
const ZED_SIM = 'http://localhost:8642/mouth-playground.html';

// His face sits in the left ~860×790 of the playground page (the page's own buttons start just below);
// the cam shows a 3:4 slice from his brows to just under his mouth.
const SimCam = () => {
    const sliceH = 820, scale = CAM_H / sliceH, sliceW = CAM_W / scale;
    return <iframe title="zed-cam" allow="autoplay"
        src={`${ZED_SIM}?mirror=1&engine=${encodeURIComponent(engineUrl())}`}
        style={{position: 'absolute', top: 24 * scale, left: -(430 - sliceW / 2) * scale, width: 1400, height: 900, border: 0,
            transform: `scale(${scale})`, transformOrigin: 'top left', pointerEvents: 'none'}}/>;
};

const LiveCam = () => {
    const ref = useRef(null);
    const [error, setError] = useState(null);
    useEffect(() => {
        let stream = null;
        navigator.mediaDevices.getUserMedia({video: {width: 1920, height: 1080}, audio: false})
            .then(s => { stream = s; if (ref.current) ref.current.srcObject = s; })
            .catch(e => setError(e.message));
        return () => stream && stream.getTracks().forEach(t => t.stop());
    }, []);
    if (error) return <div style={{color: '#888', fontSize: 22, padding: 30}}>Zed cam: {error}</div>;
    return <video ref={ref} autoPlay muted playsInline style={{width: '100%', height: '100%', objectFit: 'cover'}}/>;
};

// The camera on his face, nothing drawn around it: on the night this is a video frame.
const ZedCam = () => <div style={{position: 'absolute', left: CAM_X, top: CAM_Y, width: CAM_W, height: CAM_H, borderRadius: 18,
    overflow: 'hidden', background: '#0b0907', boxShadow: '0 20px 70px rgba(0,0,0,.8)'}}>
    {camSource() === 'camera' ? <LiveCam/> : <SimCam/>}
</div>;

// The picture is always exactly 1920×1080, scaled to the window and letterboxed: a maximized window
// with a toolbar (or a projector at another resolution) shows the same frame, never a cropped one.
const Frame1080 = ({children}) => {
    const [scale, setScale] = useState(1);
    useEffect(() => {
        const fit = () => setScale(Math.min(window.innerWidth / W, window.innerHeight / H));
        fit();
        window.addEventListener('resize', fit);
        return () => window.removeEventListener('resize', fit);
    }, []);
    return <div style={{position: 'fixed', inset: 0, background: '#000', overflow: 'hidden'}}>
        <div style={{position: 'absolute', left: '50%', top: '50%', width: W, height: H, marginLeft: -W / 2, marginTop: -H / 2,
            transform: `scale(${scale})`, color: '#fff', overflow: 'hidden'}}>
            {children}
        </div>
    </div>;
};

const sideLayout = (n) => {
    const cols = n > 12 ? 3 : 2, rows = Math.max(1, Math.ceil(n / cols));
    const size = Math.max(56, Math.min(150, Math.floor((CAM_H + 14) / rows) - 14));
    return {cols, size};
};

const Portrait = ({person, size, x, y, featured, dim, filed}) => <div style={{
    position: 'absolute', left: x, top: y, width: size, height: size, borderRadius: size / 2, overflow: 'hidden',
    opacity: featured ? 0 : dim ? 0.28 : 1, transition: 'opacity 400ms, box-shadow 400ms',
    boxShadow: filed ? `0 0 0 4px rgba(${ZED_RED},.85)` : '0 0 0 2px rgba(255,255,255,.18)', background: '#111'}}>
    <img alt="" src={person.image_url} style={{width: '100%', height: '100%', objectFit: 'cover'}}/>
    {filed && <div style={{position: 'absolute', left: 0, right: 0, bottom: size * 0.12, textAlign: 'center', fontSize: Math.max(11, size * 0.13),
        fontWeight: 900, letterSpacing: 2, color: '#000', background: `rgba(${ZED_RED},.9)`, transform: 'rotate(-8deg)'}}>FILED</div>}
</div>;

const FeaturedCard = ({person, side}) => {
    const x = side === 'left' ? CAM_X - 40 - 400 : CAM_X + CAM_W + 40;
    return <div style={{position: 'absolute', left: x, top: CAM_Y + 60, width: 400, padding: '24px 20px', borderRadius: 18, textAlign: 'center',
        background: 'rgba(10,10,12,.92)', boxShadow: `0 20px 60px rgba(0,0,0,.7), 0 0 0 2px rgba(${ZED_RED},.5)`,
        animation: `zed-card-in-${side} 420ms cubic-bezier(.2,.9,.3,1.2)`}}>
        <img alt="" src={person.image_url} style={{width: 340, height: 340, objectFit: 'cover', borderRadius: 12}}/>
        <div style={{fontSize: 48, fontWeight: 800, marginTop: 14}}>{person.name}</div>
        {person.nickname && <div style={{fontSize: 32, fontWeight: 700, color: `rgb(${ZED_RED})`}}>{person.nickname}</div>}
        {person.interesting_fact && <div style={{fontSize: 22, opacity: 0.8, marginTop: 8}}>{person.interesting_fact}</div>}
    </div>;
};

const CSS = `
@keyframes zed-card-in-left { from { opacity: 0; transform: translateX(-80px) scale(.9); } to { opacity: 1; transform: none; } }
@keyframes zed-card-in-right { from { opacity: 0; transform: translateX(80px) scale(.9); } to { opacity: 1; transform: none; } }
`;

// Zed turning toward whoever he's talking about is the server's doing (dialog_director.rb); this only draws.
export const ZedStage = ({cast, featured, filed, subtitle}) => {
    const left = cast.filter((_, i) => i % 2 === 0), right = cast.filter((_, i) => i % 2 === 1);
    const side = featured ? (right.some(p => p.name === featured.name) ? 'right' : 'left') : null;

    const gallery = (people, which) => {
        const {cols, size} = sideLayout(people.length), gap = 14;
        const blockW = cols * size + (cols - 1) * gap, rows = Math.ceil(people.length / cols);
        const x0 = which === 'left' ? 70 : W - 70 - blockW, y0 = CAM_Y + (CAM_H - (rows * size + (rows - 1) * gap)) / 2;
        return people.map((p, i) => <Portrait key={`${which}-${p.name}`} person={p} size={size}
            x={x0 + (i % cols) * (size + gap)} y={y0 + Math.floor(i / cols) * (size + gap)}
            featured={featured && featured.name === p.name} dim={!!featured && side === which} filed={filed.has(p.name)}/>);
    };

    return <Frame1080>
        <style>{CSS}</style>
        <div style={{position: 'absolute', left: 160, right: 160, top: 40, height: 220, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', textAlign: 'center'}}>
            {subtitle}
        </div>
        {gallery(left, 'left')}
        {gallery(right, 'right')}
        <ZedCam/>
        {featured && <FeaturedCard key={featured.name} person={featured} side={side}/>}
    </Frame1080>;
};
